/** 상태 → 칩(색조 + 라벨) 매핑.
 *
 * **앱 업로드 큐와 웹 작업 표가 같은 칩을 쓴다**(PRD 7.5). 매핑이 화면에 있으면 같은
 * 상태가 앱에서는 "완료", 웹에서는 "성공"으로 보이는 일이 생긴다. 라벨 문구는 목업에서
 * 그대로 가져왔다.
 *
 * 색조 4종은 늘리지 않는다 — 훑어서 구분하는 것이 칩의 목적이라 종류가 늘면 못 훑는다.
 */

import type { DetailJobStatus, DetailJobView, JobImageView, PaymentStatus } from "./domain";
import { failureMessage } from "./failure-messages";
import { formatCount } from "./format";

export type StatusTone = "ok" | "run" | "fail" | "idle";

export interface StatusLabel {
  tone: StatusTone;
  label: string;
}

/** 작업 한 줄의 칩 (PRD 7.5.8: `초안` · `만드는 중` · `준비됨` · `내보냄` · `실패`).
 *
 * ⚠️ `DetailJobStatus` 에 값을 더하면 여기에도 `case` 를 더해야 한다. `default` 가 있어
 * 컴파일은 통과하지만 새 상태가 전부 `초안` 칩을 달고 나간다 — 결제·내보내기가 사라져 보인다. */
export function jobStatusLabel(status: DetailJobStatus): StatusLabel {
  switch (status) {
    case "READY":
      return { tone: "ok", label: "준비됨" };
    // 내보낸 작업도 결과가 있는 상태라 `준비됨`과 같은 색조다(PRD 7.5.8).
    case "EXPORTED":
      return { tone: "ok", label: "내보냄" };
    // 결제만 끝나고 시작 전이어도 처리 확약 구간이라 셀러에게는 `만드는 중`이다(PRD S3b ②).
    case "PAID":
    case "PROCESSING":
      return { tone: "run", label: "만드는 중" };
    case "FAILED":
      return { tone: "fail", label: "실패" };
    // 원본을 지웠어도 목록에서 사라지지 않는다 — 흔적이 없으면 셀러가 올린 것을 찾지 못한다
    // (PRD 5. ①). 실패가 아니므로 fail 이 아니라 idle 이다. 최종 문구는 O-22 미결.
    case "DISCARDED":
      return { tone: "idle", label: "사진 지워짐" };
    case "UPLOADED":
    default:
      // 사진은 올라갔지만 아직 만들지 않은 상태다. 목업은 이 줄을 `초안`으로 부른다.
      return { tone: "idle", label: "초안" };
  }
}

/** 청구 내역 한 줄의 칩 (PRD 7.5.9 용어 표 · 7.6.21 칩 표).
 *
 * 표에 확정된 것은 **`결제 전`**(2026-09-13 신설) · `완료` · `일부 돌아감` · `전부 돌아감`
 * 과 `AUTHORIZED` 의 **`결제됨`**(2026-09-08 신설)이고, 나머지는 같은 색조 4종 안에서 채웠다.
 *
 * **시대가 둘이다**(PRD 7.6.21). 결제가 붙기 전에는 `완료` 도 `돌아감` 도 참일 수 없다.
 * 돈이 나간 적이 없다. 지금 실제로 나오는 칩은 `결제 전` 하나다.
 *
 * ⚠️ **`청구 없음` 은 이 함수가 정할 수 없다.** 그 칩은 표의 다섯째 줄인데 판정 기준이
 * **상태가 아니라 금액**이다(`PENDING` 인데 청구될 금액이 0. 전량 실패·시작 전 취소가 그
 * 자리다). 상태 하나만 받는 이 함수에 금액 인자를 얹지 않는다. 축이 둘인 판정을 한 함수에
 * 넣으면 호출부마다 다르게 부르게 된다. **그 칩이 필요한 화면은 `PaymentHistoryItemView`
 * 를 통째로 들고 있으므로 그 자리에서 갈라야 하고, 낱말은 여기서 즉석에 고르지 말고
 * PRD 7.6.21 칩 표를 그대로 읽는다.**
 *
 * ⚠️ **여기에 `환불`을 쓰지 않는다.** 그것은 문서·코드의 개념어이고 셀러가 보는 자리에는
 * 나오지 않는다(PRD 7.5.9). 타입 값 이름(`PARTIALLY_REFUNDED`·`REFUNDED`)은 그대로 두고
 * 라벨만 표의 낱말로 돌려준다 — 컬럼명까지 바꾸면 스키마가 화면 문구를 따라다니게 된다. */
export function paymentStatusLabel(status: PaymentStatus): StatusLabel {
  switch (status) {
    case "CAPTURED":
      return { tone: "ok", label: "완료" };
    case "PARTIALLY_REFUNDED":
      return { tone: "run", label: "일부 돌아감" };
    case "REFUNDED":
      return { tone: "idle", label: "전부 돌아감" };
    // 이 둘은 7.6.21 칩 표의 다섯 줄에 없다. **빠진 것이 아니라 그 표가 그리는 두 시대
    // 밖이다.** 결제가 성립하지 않은 줄이라 `결제 전`·`돌아감` 어느 쪽도 아니다. 낱말은
    // 용어 표에서 가져왔다(`취소` = 셀러가 스스로 무르는 일. `A-27` 이 `결제 실패` 를
    // 화면 이름으로 이미 쓴다). **표에 없다고 표의 낱말로 끌어다 붙이지 마라.**
    case "FAILED":
      return { tone: "fail", label: "결제 실패" };
    case "CANCELED":
      return { tone: "idle", label: "취소" };
    // 매입 전이라 `완료`로 쓰면 안 된다. 셀러 명세와 어긋난다. `승인`도 쓰지 않는다.
    // 그 낱말은 셀러가 결과를 확정해 판을 만드는 동작에 예약됐고(`7장 승인 → v1`),
    // 한 낱말이 "카드사가 승인했다"와 "셀러가 승인했다"를 함께 가리키면 이력 줄에서
    // 둘을 가릴 수 없다. `결제됨`은 목업 `A-28`(`결제됨 · 시작 전`)이 이미 쓰는 낱말이고
    // O-13 이 (가)/(나) 어느 쪽으로 닫혀도 참이다(PRD 7.5.9 용어 표, 확정 2026-09-08).
    case "AUTHORIZED":
      return { tone: "run", label: "결제됨" };
    // **`결제 대기` 에서 바꿨다 (2026-09-14. PRD 7.5.9 · 7.6.21 이 넘긴 항목).**
    // `대기` 는 곧 결제된다고 말하는데 **결제사가 아직 없다**(13.1 O-13). `청구 예정` 을
    // 버린 것과 같은 이유이고, 용어 표는 `결제 대기` 를 쓰지 않는 말 칸에 올려 뒀다.
    // `결제 전` 은 **소급 청구가 어느 쪽으로 정해져도 참이다**(7.13 D-71). 작업 바가 이미
    // 쓰는 낱말이라 새로 고른 것도 아니다(7.7.0).
    //
    // **색조도 idle 에서 run 으로 바뀐다**(7.6.21 칩 표). 아직 끝나지 않은 돈이라
    // `취소`·`전부 돌아감` 과 같은 색이면 훑는 셀러는 끝난 줄로 읽는다.
    case "PENDING":
    default:
      return { tone: "run", label: "결제 전" };
  }
}

/** 서버가 아는 장별 상태의 칩 (목업 A-22 · A-24: `완료` · `처리` · `실패` · `대기`).
 *
 * 0002 가 `PROCESSING` 을 더해 목업 A-24 의 `처리` 칩이 서버 데이터로 성립한다 —
 * 예전에는 그 상태가 없어 처리 중인 장도 `대기`로 보였다.
 *
 * ## 다섯째 칩 `멈춤` 은 상태값이 아니라 시간이 만든다 (2026-09-14)
 *
 * `stalled` 를 켜면 `PROCESSING` 한 상태가 칩 둘로 갈린다(`처리` · `멈춤`). **서버 상태값을
 * 늘리는 일이 아니다.** `JobImageStatus` 는 그대로 넷이고, **같은 `PROCESSING` 을 화면이
 * 시간으로 갈라 부르는 것**이다(PRD `7.6.7a`). 목업 `A-24b` 가 그 칩을 `chip run` 으로
 * 그리고 보조줄을 `응답을 기다리는 중` 으로 적어 뒀다.
 *
 * ⚠️ **색조를 `fail` 로 내리지 않는다.** 멈춘 장은 아직 잃은 장이 아니다. 서버가 임대 만료를
 * 회수하면 그 장은 `대기` 로 돌아가 다시 만들어진다(`reclaimStrandedImages`). `fail` 로
 * 그리면 셀러는 못 만든 장으로 읽고, 그 판단에 따라 `A-25`(값이 드는 다시 만들기)를 누른다.
 *
 * ⛔ **판정을 이 함수 안에서 하지 않는다.** 멈춤은 "선점한 지 얼마나 지났나" 로 정해지는데,
 * 그 시각은 서버가 쥐고 있고 이 함수는 장 하나만 본다. **호출부가 판정하고 결과만 넘긴다.**
 *
 * ⚠️ **판정 문턱은 서버의 임대와 같아야 한다**(PRD `7.6.7a`. `STALL_AFTER_MS >=
 * PROCESSING_LEASE_SECONDS`). 2026-09-14 에 화면 쪽이 3분에서 5분으로 올라가 둘이 맞았다.
 * 어긋나 있던 동안에는 **멈췄다고 말하면서 풀어 주지는 못하는 창**이 있었고, 그 창에서
 * `다시 확인` 을 누른 셀러에게는 아무 일도 일어나지 않는다. **두 숫자가 아직 두 파일에
 * 흩어져 있다**(`apps/mobile/src/app/job/[jobId].tsx` 의 `STALL_AFTER_MS` ·
 * `apps/api/src/routes/jobs.ts` 의 `PROCESSING_LEASE_SECONDS`). 한 자리로 모으는 것은
 * `job-rules.ts` 가 열리는 회차의 일이다. */
export function imageStatusLabel(
  status: JobImageView["status"],
  options: { stalled?: boolean } = {},
): StatusLabel {
  switch (status) {
    case "DONE":
      return { tone: "ok", label: "완료" };
    case "PROCESSING":
      return options.stalled ? { tone: "run", label: "멈춤" } : { tone: "run", label: "처리" };
    case "FAILED":
      return { tone: "fail", label: "실패" };
    case "PENDING":
    default:
      return { tone: "idle", label: "대기" };
  }
}

/** 업로드 큐 한 장의 클라이언트 상태. **서버 상태와 다른 축이다** — 아직 서버에 도착하지도
 * 않은 장의 상태라서 `JobImageStatus` 로 표현할 수 없다.
 *
 * 앱·웹·(드래그앤드롭) 세 입력 경로가 같은 큐로 합류하고 행 모양·칩·재시도 단위가 같아야
 * 하므로(PRD 7.8 4번) 이 축도 공통에 둔다. */
export type UploadItemState = "QUEUED" | "SENDING" | "UPLOADED" | "REJECTED";

/** 목업 `A-22` 의 칩 문구.
 *
 * **⚠️ 이 주석이 한동안 거짓이었다 (2026-09-09 정정).** `SENDING` 을 `전송` 이라고 부르고
 * 있었는데 **정본 목업과 PRD 는 둘 다 `올리는 중`** 이고, **`전송` 은 용어 표가 금지한
 * 낱말**이다(PRD 7.5.9 — 구현 용어를 화면에 노출하지 않는다). 주석이 "목업 그대로"라고
 * 적고 있어서 다음 사람이 대조하지 않게 되어 있었다.
 *
 * **보조줄과 칩이 같은 낱말을 쓰는 것은 의도한 것이다** — 칩은 상태를 훑는 자리이고
 * 보조줄은 거기에 진행률을 붙이는 자리라 하는 일이 다르다(`uploadSubline`). */
export function uploadItemLabel(state: UploadItemState): StatusLabel {
  switch (state) {
    case "UPLOADED":
      return { tone: "ok", label: "완료" };
    case "SENDING":
      return { tone: "run", label: "올리는 중" };
    case "REJECTED":
      return { tone: "fail", label: "실패" };
    case "QUEUED":
    default:
      return { tone: "idle", label: "대기" };
  }
}

/**
 * 행의 보조줄 — **상태의 사유를 말하는 자리다**(PRD `7.6.6` · `7.5.9`).
 *
 * **⚠️ 이 머리말이 `7.6.4` 를 근거로 들고 있었다 (2026-09-20 정정).** 그 절은 촬영
 * 화면이다. 업로드 큐 행의 정본은 `7.6.6` 이고, 보조줄 문자열 표는 `7.5.9` 의 신설 블록
 * "올리는 동안 장이 갖는 상태"다.
 *
 * **진행률은 칩이 아니라 보조줄에 쓴다.** 칩은 4종 고정이라 상태를 훑기 쉽고, 변하는
 * 숫자는 옆줄에 두는 편이 시선이 덜 흔들린다.
 *
 * ## `progress` 의 `null` 은 **아직 모른다**는 뜻이다 (2026-09-20)
 *
 * ⛔ **`options.progress ?? 0` 을 되살리지 마라.** 이 자리가 실기기 버그였다. XHR 진행률
 * 이벤트가 네이티브에서 오는지 자체가 확인되지 않았는데 코드가 **재지 못한 값을 `0` 으로
 * 단정**해서, 행이 `올리는 중 · 0%` 에 멈춰 있었다. 한 장이 2초 안팎이라 숫자가 안 움직이면
 * 셀러는 멈춘 것으로 읽는다. **"아직 한 번도 못 쟀다"와 "정말 0% 다"가 같은 글자였다.**
 * 같은 규율을 `apps/mobile/src/lib/session-token.ts` 가 먼저 세웠다 — `null` 은 없다,
 * `undefined` 는 아직 안 읽었다. 여기서는 **둘 다 "못 쟀다"** 이고, 못 쟀으면 숫자를 쓰지
 * 않는다. 기본값으로 메우는 순간 화면이 재지 않은 것을 잰 것처럼 말한다.
 *
 * ## 표시 범위는 `1%`~`99%` 다 (design-manager 확정 2026-09-20)
 *
 * - **`0%` 는 화면에 존재하지 않는 값이다.** 반올림이 0 이면 1 로 올린다. 0 을 쓰면 위의
 *   거짓말이 "쟀는데도 안 움직인다"는 모습으로 되돌아온다.
 * - **`100%` 를 쓰지 않는다.** 바이트를 다 보낸 뒤에도 서버 답을 기다리는 창이 있어, 그
 *   창에 `100%` 가 멈춰 서 있으면 같은 병이 반대쪽 끝에서 되살아난다. **다 보냈다는 말은
 *   `완료` 칩이 한다.**
 *
 * ⛔ **`Platform` 으로 가르지 않는다.** 갈리는 것은 플랫폼이 아니라 **잰 값이 있느냐**다.
 * 브라우저 XHR 은 사실상 늘 숫자를 주고 앱은 안 줄 수 있지만, 그것은 데이터가 다른 것이지
 * 규칙이 다른 것이 아니다. 규칙을 플랫폼으로 가르면 같은 상태가 두 화면에서 다르게 보인다.
 */
export function uploadSubline(
  state: UploadItemState,
  options: { progress?: number | null; role?: string; reason?: string } = {},
): string {
  switch (state) {
    case "SENDING": {
      const ratio = options.progress;
      // `null`(못 쟀다)과 미지정을 같게 본다. 숫자가 없으면 숫자를 쓰지 않는다.
      // `NaN`·`Infinity` 도 같은 칸이다 — XHR 이 전체 크기를 모를 때(`lengthComputable`
      // 이 거짓) 나오는 값이고, 그것도 **재지 못한 것**이다. 클램프에 그대로 넣으면
      // `NaN%` 가 그려지거나 `99%` 에 얼어붙는다.
      if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return "올리는 중";
      return `올리는 중 · ${Math.min(99, Math.max(1, Math.round(ratio * 100)))}%`;
    }
    case "UPLOADED":
      // 역할(대표컷/상세컷)은 순서에서 나온다 — 첫 장이 대표컷이다.
      return options.role ?? "완료";
    case "REJECTED":
      return options.reason ?? "올리지 못했어요";
    case "QUEUED":
    default:
      return "대기 중";
  }
}

/** 첫 장이 대표컷이다(`docs/TEMPLATE.md` 5장). 목업 A-22 의 보조줄이 쓰는 말. */
export function imageRoleLabel(sortOrder: number): string {
  return sortOrder === 0 ? "대표컷" : "상세컷";
}

/** 블록 종류 → **셀러가 읽는 말**. 정본은 PRD `7.7.4` 의 토글 다섯(`대표컷` · `상세컷` ·
 *  `디테일 크롭` · `스펙표` · `유의사항`)이고, 같은 낱말이 `7.9` 타임라인의
 *  `유의사항 블록 끔` 에도 그대로 쓰인다.
 *
 * **여기로 올린 이유는 이 표가 이미 두 벌이었기 때문이다.** 웹(예전 `lib/sample-data.ts`)과
 * 앱(`apps/mobile/src/lib/template.ts`)이 각자 들고 있었고, 한 번 갈려서 **같은 블록을 앱과
 * 웹이 다른 이름으로 불렀다.** 서버가 이력 줄에 이 낱말을 쓰면서 세 벌이 될 자리라 정본을
 * shared 에 둔다. 지금은 서버 · 웹 · 앱이 모두 이 함수를 읽는다. **화면 쪽에 이름 표를
 * 다시 두지 않는다.**
 *
 * ⛔ **블록 종류값(`notice`)을 셀러 화면에 그대로 내보내지 않는다**(용어 규칙: 구현 용어를
 * 화면에 노출하지 않는다). 모르는 종류일 때만 종류값을 쓴다 — 조용히 빈 칸을 내는 것보다
 * 낫고, 그 자리가 곧 "규격에 블록이 늘었는데 낱말을 안 정했다"는 신호다. */
const BLOCK_LABELS: Record<string, string> = {
  hero: "대표컷",
  gallery: "상세컷",
  "detail-crop": "디테일 크롭",
  spec: "스펙표",
  notice: "유의사항",
};

export function blockLabel(blockType: string): string {
  return BLOCK_LABELS[blockType] ?? blockType;
}

/** 서버가 아는 장별 상태의 보조줄. 실패는 코드가 아니라 셀러 말로 쓴다.
 *
 * `stalled` 는 위 `imageStatusLabel` 과 같은 값이다. **한 행의 칩과 보조줄이 다른 판정을
 * 들고 있으면 안 되므로 호출부가 같은 값을 둘 다에 넘긴다.** 문구는 목업 `A-24b` 의
 * `응답을 기다리는 중` 이고 PRD `7.5.9` 장 상태 표가 정본이다. */
export function imageSubline(image: JobImageView, options: { stalled?: boolean } = {}): string {
  if (image.status === "FAILED") return failureMessage(image.errorCode).short;
  if (image.status === "DONE") return imageRoleLabel(image.sortOrder);
  // 나가 있는 장을 `대기 중`이라 쓰면 셀러가 멈춘 줄 안다.
  // 멈춘 장에 `배경 지우는 중` 을 계속 쓰면 그 거짓말이 5분을 넘겨 이어진다. 그 장은 지금
  // 배경을 지우고 있지 않고, 우리도 공급자가 무엇을 하고 있는지 모른다.
  if (image.status === "PROCESSING") return options.stalled ? "응답을 기다리는 중" : "배경 지우는 중";
  return "대기 중";
}

/** 진행 헤더의 `5 / 7장`. */
export function progressCountLabel(job: DetailJobView): string {
  const done = job.images.filter((image) => image.status === "DONE").length;
  return formatCount(done, job.images.length);
}
